import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getAllUsers,
  toggleUserActive,
  toggleFoundingCoach,
  backfillFoundingCoaches,
  verifyCoach,
  rejectCoachVerification,
  type AdminUserData,
  type Actor,
} from '../../services/admin';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import type { CoachVerificationStatus } from '../../types/user';
import AdminEditProfileModal from '../../components/AdminEditProfileModal';

type FilterType = 'all' | 'coach' | 'player' | 'pending';

export default function AdminUsers() {
  const { currentUser, userProfile } = useAuth();
  const [users, setUsers] = useState<AdminUserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [rejectModalUser, setRejectModalUser] = useState<AdminUserData | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showBackfill, setShowBackfill] = useState(false);
  const [editProfileUser, setEditProfileUser] = useState<AdminUserData | null>(null);
  const { showSuccess, showError } = useToast();

  const actor: Actor | undefined = currentUser && userProfile?.role
    ? { id: currentUser.uid, name: userProfile.displayName || currentUser.email || 'Admin', role: userProfile.role }
    : undefined;

  useEffect(() => {
    loadUsers();
  }, [filter]);

  async function loadUsers() {
    try {
      setLoading(true);
      const roleFilter = filter === 'all' || filter === 'pending' ? undefined : filter;
      const data = await getAllUsers(roleFilter);
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
      showError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleActive(userId: string, currentStatus: boolean) {
    try {
      await toggleUserActive(userId, !currentStatus, actor);
      showSuccess(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      loadUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      showError('Failed to update user status');
    }
  }

  async function handleToggleFoundingCoach(coachId: string, currentStatus: boolean) {
    try {
      await toggleFoundingCoach(coachId, !currentStatus, actor);
      showSuccess(`Coach ${!currentStatus ? 'marked as' : 'removed from'} Founding Coach`);
      loadUsers();
    } catch (error) {
      console.error('Error toggling founding coach status:', error);
      showError('Failed to update founding coach status');
    }
  }

  async function handleVerifyCoach(coachId: string) {
    try {
      await verifyCoach(coachId, actor);
      showSuccess('Coach verified successfully');
      loadUsers();
    } catch (error) {
      console.error('Error verifying coach:', error);
      showError('Failed to verify coach');
    }
  }

  async function handleRejectCoach() {
    if (!rejectModalUser) return;

    try {
      await rejectCoachVerification(rejectModalUser.uid, rejectReason || 'Application not approved', actor);
      showSuccess('Coach verification rejected');
      setRejectModalUser(null);
      setRejectReason('');
      loadUsers();
    } catch (error) {
      console.error('Error rejecting coach:', error);
      showError('Failed to reject coach');
    }
  }

  // Get verification status from user
  const getVerificationStatus = (user: AdminUserData): CoachVerificationStatus | null => {
    return user.coachProfile?.verificationStatus || null;
  };

  // Filter users based on filter type
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // Apply pending filter
    if (filter === 'pending') {
      return getVerificationStatus(user) === 'pending';
    }

    return true;
  });

  // Count pending verifications
  const pendingCount = users.filter(u => getVerificationStatus(u) === 'pending').length;

  // Render verification badge
  const renderVerificationBadge = (user: AdminUserData) => {
    const status = getVerificationStatus(user);
    if (!status || !user.coachProfile) return null;

    const badgeClasses: Record<CoachVerificationStatus, string> = {
      none: 'bg-gray-100 text-gray-600',
      pending: 'bg-orange-100 text-orange-800',
      verified: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };

    const badgeLabels: Record<CoachVerificationStatus, string> = {
      none: 'Not Applied',
      pending: 'Pending',
      verified: 'Verified',
      rejected: 'Rejected',
    };

    return (
      <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeClasses[status]}`}>
        {badgeLabels[status]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">User & Coach Management</h1>
          <p className="text-gray-600">View and manage all users and coaches</p>
        </div>
        <button
          onClick={() => setShowBackfill(true)}
          className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg text-sm font-medium transition"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
          Backfill Founding Coaches
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col md:flex-row gap-4">
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === 'all'
                ? 'bg-tennis-green text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            All Users ({users.length})
          </button>
          <button
            onClick={() => setFilter('coach')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === 'coach'
                ? 'bg-tennis-green text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Coaches
          </button>
          <button
            onClick={() => setFilter('player')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === 'player'
                ? 'bg-tennis-green text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Players
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition relative ${
              filter === 'pending'
                ? 'bg-orange-500 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Pending Verification
            {pendingCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {pendingCount}
              </span>
            )}
          </button>
        </div>

        <input
          type="text"
          placeholder="Search by name, email, or username..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tennis-green input-glow"
        />
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => {
                const verificationStatus = getVerificationStatus(user);
                const hasCoachProfile = !!user.coachProfile;

                return (
                  <tr key={user.uid} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          {user.avatarUrl ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={user.avatarUrl}
                              alt={user.displayName || 'User'}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-500 font-medium">
                                {(user.displayName || user.email || 'U')[0].toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <Link
                            to={`/${user.accountType}/${user.uid}`}
                            className="text-sm font-medium text-tennis-green hover:text-tennis-ball no-underline"
                          >
                            {user.displayName || user.username || 'Unnamed User'}
                          </Link>
                          <div className="flex items-center gap-1 flex-wrap">
                            {user.coachProfile?.foundingCoach && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                Founding Coach
                              </span>
                            )}
                            {hasCoachProfile && renderVerificationBadge(user)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.accountType === 'coach'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {user.accountType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.isActive !== false
                            ? 'bg-green-100 text-green-800'
                            : user.deactivatedByUser
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.isActive !== false
                          ? 'Active'
                          : user.deactivatedByUser
                            ? 'Deleted (self)'
                            : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2 flex-wrap">
                        <button
                          onClick={() => setEditProfileUser(user)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                        >
                          Edit Profile
                        </button>
                        {user.role !== 'root' && (
                          <button
                            onClick={() => handleToggleActive(user.uid, user.isActive !== false)}
                            className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md ${
                              user.isActive !== false
                                ? 'text-red-700 bg-red-100 hover:bg-red-200'
                                : 'text-green-700 bg-green-100 hover:bg-green-200'
                            }`}
                          >
                            {user.isActive !== false ? 'Deactivate' : 'Activate'}
                          </button>
                        )}

                        {/* Coach verification actions */}
                        {hasCoachProfile && verificationStatus === 'pending' && (
                          <>
                            <button
                              onClick={() => handleVerifyCoach(user.uid)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200"
                            >
                              Verify
                            </button>
                            <button
                              onClick={() => setRejectModalUser(user)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200"
                            >
                              Reject
                            </button>
                          </>
                        )}

                        {/* Founding coach toggle for verified coaches */}
                        {(user.accountType === 'coach' || verificationStatus === 'verified') && (
                          <button
                            onClick={() => handleToggleFoundingCoach(user.uid, user.coachProfile?.foundingCoach || false)}
                            className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md ${
                              user.coachProfile?.foundingCoach
                                ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                                : 'text-amber-700 bg-amber-100 hover:bg-amber-200'
                            }`}
                          >
                            {user.coachProfile?.foundingCoach ? 'Remove Founding' : 'Mark Founding'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {filter === 'pending'
              ? 'No pending coach verifications'
              : 'No users found matching your criteria'}
          </div>
        )}
      </div>

      {/* Backfill Founding Coaches Modal */}
      {showBackfill && (
        <BackfillFoundingCoachesModal actor={actor} onClose={() => { setShowBackfill(false); loadUsers(); }} />
      )}

      {/* Edit Profile Modal */}
      {editProfileUser && (
        <AdminEditProfileModal
          user={editProfileUser}
          onClose={() => setEditProfileUser(null)}
          onSaved={loadUsers}
        />
      )}

      {/* Reject Modal */}
      {rejectModalUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Reject Coach Verification
            </h3>
            <p className="text-gray-600 mb-4">
              You are about to reject the coach verification for{' '}
              <strong>{rejectModalUser.displayName || rejectModalUser.email}</strong>.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for rejection (optional)
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tennis-green input-glow"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setRejectModalUser(null);
                  setRejectReason('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectCoach}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Backfill Founding Coaches Modal ───────────────────────────────────────────

type BackfillResult = { updated: string[]; skipped: number; errors: number };

function BackfillFoundingCoachesModal({ actor, onClose }: { actor: Actor | undefined; onClose: () => void }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<BackfillResult | null>(null);
  const { showError } = useToast();

  const handleRun = useCallback(async () => {
    setRunning(true);
    try {
      const r = await backfillFoundingCoaches(actor);
      setResult(r);
      if (r.errors) showError(`${r.errors} coach(es) failed to update`);
    } catch (err) {
      console.error('Backfill failed:', err);
      showError('Backfill failed — check console for details');
    } finally {
      setRunning(false);
    }
  }, [actor, showError]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Backfill Founding Coaches</h2>
          <button onClick={onClose} disabled={running} className="text-gray-400 hover:text-gray-700 disabled:opacity-50">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4">
          {result ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                </svg>
                <span className="text-sm font-medium">
                  Done — {result.updated.length} updated, {result.skipped} already set
                  {result.errors > 0 && `, ${result.errors} error${result.errors !== 1 ? 's' : ''}`}
                </span>
              </div>
              {result.updated.length > 0 && (
                <ul className="space-y-0.5 text-xs text-gray-700 max-h-48 overflow-y-auto">
                  {result.updated.map((name) => <li key={name}>✓ {name}</li>)}
                </ul>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Sets <code className="bg-gray-100 px-1 rounded text-xs">coachProfile.foundingCoach = true</code> on
                every verified coach that doesn't already have it. Skips coaches already flagged.
              </p>
              <p className="text-xs text-gray-500">
                Idempotent — safe to run multiple times. Individual coaches can also be toggled per-row below.
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={running}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={handleRun}
              disabled={running}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm disabled:opacity-50 flex items-center gap-2"
            >
              {running && <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
              {running ? 'Running…' : 'Run Backfill'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
